import { useLocation, useNavigate } from "@tanstack/react-router";
import { SettingsIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { cn } from "~/lib/utils";
import { accountDisplayName, accountIdentityAside } from "~/neucleos/accountIdentity";
import { selectProfileWorldKey, useActorProfile } from "~/neucleos/actorProfileStore";
import { useNeucleosWorldStore } from "~/neucleos/worldStore";
import { openWorldRoomAccount, openWorldViewSettings } from "~/neucleos/worldViewController";
import { Avatar } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import { resolveNeucleosFooterStatus } from "~/components/NeucleosWorldFrame";
import {
  readStatusRefreshingWorlds,
  startNeucleosSignIn,
} from "~/components/settings/NeucleosConnectionSettings";

/** Shared account doors for the context dock and pages outside the session layout. */
export function DockAccountControls({ wide }: { readonly wide: boolean }) {
  const navigate = useNavigate();
  const pathname = useLocation({ select: (location) => location.pathname });
  const identity = useNeucleosWorldStore((state) => state.identity);
  const signedIn = useNeucleosWorldStore((state) => state.signedIn);
  const loaded = useNeucleosWorldStore((state) => state.loaded);
  // US-CAN-08 (the principal, 2026-09-10): the chip wears the person's NAME
  // from their Actor profile and their photo; the sign-in email is the
  // last resort for the label and otherwise rides the tooltip.
  const profileWorldKey = useNeucleosWorldStore(selectProfileWorldKey);
  const profile = useActorProfile(profileWorldKey);
  const accountName = accountDisplayName(profile, identity);
  const accountAside = accountIdentityAside(accountName, identity);
  const footerStatus = resolveNeucleosFooterStatus({ loaded, signedIn });
  const [signInPending, setSignInPending] = useState(false);
  const stopSignInWatch = useRef<(() => void) | null>(null);
  useEffect(() => () => stopSignInWatch.current?.(), []);
  const settingsActive = pathname === "/settings" || pathname.startsWith("/settings/");
  return (
    <>
      <Button
        aria-current={settingsActive ? "page" : undefined}
        aria-label="Settings"
        title={wide ? undefined : "Settings"}
        className={cn(
          "shrink-0 rounded-[10px] border border-border/70",
          wide ? "h-7 w-full min-w-0 justify-start gap-2 px-2" : "size-7",
        )}
        data-shell-utility="settings"
        onClick={() => {
          if (openWorldViewSettings()) return;
          void navigate({ to: "/settings" });
        }}
        size={wide ? "sm" : "icon-sm"}
        variant="ghost"
      >
        <SettingsIcon className="size-3.5" />
        {wide ? <span className="truncate text-[11px]">Settings</span> : null}
      </Button>
      {signedIn ? (
        <Button
          aria-label={accountName ?? "Your account"}
          title={wide ? (accountAside ?? undefined) : (accountName ?? "Your account")}
          className={cn(
            "shrink-0 rounded-[10px] border border-border/70",
            wide ? "h-7 w-full min-w-0 justify-start gap-2 px-2" : "size-7",
          )}
          data-shell-utility="account"
          onClick={() => {
            if (openWorldRoomAccount()) return;
            void navigate({ to: "/settings/account" });
          }}
          size={wide ? "sm" : "icon-sm"}
          variant="ghost"
        >
          <Avatar className="size-4" label={accountName} src={profile?.avatarUrl ?? null} />
          {wide ? (
            <span className="truncate text-[11px]" data-account-chip-name="">
              {accountName ?? "Sign in"}
            </span>
          ) : null}
        </Button>
      ) : footerStatus.kind === "signed-out" ? (
        <Button
          aria-label={footerStatus.label ?? "Log in to Neucleos"}
          title={wide ? undefined : (footerStatus.label ?? "Log in to Neucleos")}
          className={cn(
            "shrink-0 rounded-[10px] border border-border/70",
            wide ? "h-7 w-full min-w-0 justify-start gap-2 px-2" : "size-7",
          )}
          data-neucleos-login-door=""
          data-shell-utility="account"
          disabled={signInPending}
          onClick={() => {
            void (async () => {
              setSignInPending(true);
              try {
                stopSignInWatch.current?.();
                stopSignInWatch.current = await startNeucleosSignIn({
                  read: readStatusRefreshingWorlds,
                  onDone: () => {},
                });
              } finally {
                setSignInPending(false);
              }
            })();
          }}
          size={wide ? "sm" : "icon-sm"}
          variant="ghost"
        >
          <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-red-500" />
          {wide ? <span className="truncate text-[11px]">{footerStatus.label}</span> : null}
        </Button>
      ) : null}
    </>
  );
}
